import type { Theme } from '@fortum/elemental-ui'
import { colors as elementalColors, generateColorLuminances } from '@fortum/elemental-ui'

export const colors = {
  ...elementalColors,

  /**
   * Ocean-Emerald 'linear-gradient(162deg, #0F7A5A 0%, #1E4F45 100%)'
   */
  oceanEmerald: `linear-gradient(162deg, #115039 0%, #0C3727 100%)`,

  /**
   * Ocean-Emerald 45 deg clockwise (-45 deg)
   */
  oceanEmeraldSE45: `linear-gradient(135deg, #0C3727 0%, #115039 100%)`,

  /**
   * Ocean-Emerald 0 deg
   */
  oceanEmeraldInverted: `linear-gradient(0deg, #115039 0%, #0C3727 100%)`,

  /**
   * Pale Lavender Purple '#F8E1FA'
   */
  paleLavenderPurple: '#F8E1FA',

  /**
   * Silent Purple '#9D5DA2'
   */
  silentPurple: '#9D5DA2',

  /**
   * Happy Purple '#B25BB8'
   */
  happyPurple: '#B25BB8',

  /**
   * Dark Turquoise '#1E494F'
   */
  darkTurquoise: '#1E494F',

  /**
   * Cloud Turquoise '#467581'
   */
  cloudTurquoise: '#467581',

  /**
   * Calm Turquoise: '#08A69E'
   */
  calmTurquoise: '#08A69E',

  /**
   * Deep Turquoise: '#0F777A'
   */
  deepTurquoise: '#0F777A',

  /**
   * Sonic Grey: '#757575',
   */
  sonicGrey: '#757575',

  /**
   * Cloud Grey: '#F2F2F2'
   */
  cloudGrey: '#F2F2F2',

  /**
   * Misty Blue: '#5377B2'
   */
  mistyBlue: '#5377B2',

  /**
   * Smurf Blue: '#547DC0'
   */
  smurfBlue: '#547DC0',

  /**
   * Composed Blue: '#325285'
   */
  composedBlue: '#325285',

  /**
   * Water Blue: '#78A0D4'
   */
  waterBlue: '#78A0D4',

  /**
   * Apple Red: '#CE4656'
   */
  appleRed: '#CE4656',

  /**
   * Wooden Beige: '#74613F'
   */
  woodenBeige: '#74613F',

  /**
   * Chocolate Brown: '#6F4416'
   */
  chocolateBrown: '#6F4416',

  /**
   * Ivy Green X3: '#146344'
   */
  ivyGreenX3: '#146344',
}

/**
 * CoS semantic colors. These should be preferred to Elemental colors to support theming.
 * https://www.figma.com/file/O4zqENJ9hbrY7vDmqLBYxN/CoS-Styles?type=design&node-id=0-1&mode=dev
 */
export type CosColor = Record<
  | 'brandPrimary'
  | 'brandSecondary'
  | 'brandInfo'
  | 'brandAlert'
  | 'brandWarning'
  | 'brandElemental'
  | 'textPrimary'
  | 'textSecondary'
  | 'textLarge'
  | 'textInverted'
  | 'textLightOnDark'
  | 'textLightOnDarkAccent'
  | 'textLightOnDarkSubtle'
  | 'textLink'
  | 'textSuccess'
  | 'textAccent'
  | 'textInfo'
  | 'textAlert'
  | 'textWarning'
  | 'textPositive'
  | 'textDisabled'
  | 'textHeading'
  | 'textHighlight'
  | 'backgroundPrimary'
  | 'backgroundSecondary'
  | 'backgroundTertiary'
  | 'backgroundLightOnDark'
  | 'backgroundLightOnDarkGradient'
  | 'backgroundSuccess'
  | 'backgroundInfo'
  | 'backgroundAlert'
  | 'backgroundWarning'
  | 'backgroundPositive'
  | 'borderPrimary'
  | 'borderSuccess'
  | 'borderInfo'
  | 'borderAlert'
  | 'borderWarning'
  | 'borderPositive'
  | `chart${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10}`
  | `chart${1 | 2 | 3 | 4 | 5 | 6 | 7}-${1 | 2 | 3}`,
  string
>

export const lightColors: CosColor = {
  /**
   * Brand colors
   */
  brandPrimary: colors.ivyGreen900,
  brandSecondary: colors.poweringGreen500,
  brandInfo: colors.broadBlue700,
  brandAlert: colors.powerRed600,
  brandWarning: colors.electricYellow400,
  brandElemental: colors.ivyGreen600,

  /**
   * Text colors
   */
  textPrimary: colors.inkGrey,
  textSecondary: colors.sonicGrey,
  textLarge: colors.ivyGreen900,
  textInverted: colors.snowWhite, // Use on backgroundTertiary
  textLightOnDark: colors.snowWhite, // Use on backgroundLightOnDark
  textLightOnDarkAccent: colors.poweringGreen500,
  textLightOnDarkSubtle: colors.ivyGreen600,
  textLink: colors.ivyGreen600,
  textSuccess: colors.ivyGreen900,
  textAccent: colors.lavenderPurple700,
  textInfo: colors.broadBlue900,
  textAlert: colors.powerRed700,
  textWarning: colors.inkGrey,
  textPositive: colors.lavenderPurple600,
  textDisabled: colors.fogGrey,
  textHeading: colors.lavenderPurple300,
  textHighlight: colors.lavenderPurple100,

  /**
   * Background colors
   */
  backgroundPrimary: colors.snowWhite,
  backgroundSecondary: colors.lightGrey,
  backgroundTertiary: colors.ivyGreen900,
  backgroundLightOnDark: colors.ivyGreen900,
  backgroundLightOnDarkGradient: colors.oceanEmerald,
  backgroundSuccess: colors.ivyGreen100,
  backgroundInfo: colors.broadBlue100,
  backgroundAlert: colors.powerRed100,
  backgroundWarning: colors.electricYellow100,
  backgroundPositive: colors.lavenderPurple100,

  /**
   * Border colors
   */
  borderPrimary: colors.fogGrey,
  borderSuccess: colors.ivyGreen900,
  borderInfo: colors.broadBlue900,
  borderAlert: colors.powerRed700,
  borderWarning: colors.inkGrey,
  borderPositive: colors.lavenderPurple700,

  /**
   * Chart colors
   */
  'chart1-1': colors.ivyGreen600,
  'chart1-2': colors.ivyGreenX3,
  'chart1-3': colors.ivyGreen900,
  'chart2-1': colors.silentPurple,
  'chart2-2': colors.grapePurple,
  'chart2-3': colors.plumPurple,
  'chart3-1': colors.mistyBlue,
  'chart3-2': colors.composedBlue,
  'chart3-3': colors.broadBlue900,
  'chart4-1': colors.appleRed,
  'chart4-2': colors.punchRed,
  'chart4-3': colors.rubyRed,
  'chart5-1': colors.goldenOrange,
  'chart5-2': colors.woodenBeige,
  'chart5-3': colors.chocolateBrown,
  'chart6-1': colors.cloudTurquoise,
  'chart6-2': colors.deepTurquoise,
  'chart6-3': colors.darkTurquoise,
  'chart7-1': colors.sonicGrey,
  'chart7-2': colors.emperorGrey,
  'chart7-3': colors.inkGrey,

  /** DEPRECATED */
  chart1: colors.oceanGreen,
  chart2: colors.plumPurple,
  chart3: colors.cushyBlue,
  chart4: colors.goldenOrange,
  chart5: colors.punchRed,
  chart6: colors.energeticRedOrange,
  chart7: colors.forestGreen,
  chart8: colors.brownBeige,
  chart9: colors.emperorGrey,
  chart10: colors.raisinGrey,
}

export const darkColors: CosColor = {
  /**
   * Brand colors
   */
  brandPrimary: colors.poweringGreen,
  brandSecondary: colors.waterBlue,
  brandInfo: colors.waterBlue,
  brandAlert: colors.softRed,
  brandWarning: colors.aureolinYellow,
  brandElemental: colors.ivyGreen600,

  /**
   * Text colors
   */
  textPrimary: colors.cloudGrey,
  textSecondary: colors.fogGrey,
  textLarge: colors.poweringGreen,
  textInverted: colors.raisinGrey, // Use on backgroundTertiary
  textLightOnDark: colors.snowWhite, // Use on backgroundLightOnDark
  textLightOnDarkAccent: colors.turquoiseGreen,
  textLightOnDarkSubtle: colors.vibrantGreen,
  textLink: colors.poweringGreen,
  textSuccess: colors.emeraldGreen,
  textAccent: colors.lavenderPurple700,
  textInfo: colors.broadBlue,
  textAlert: colors.rubyRed,
  textWarning: colors.inkGrey,
  textPositive: colors.paleLavenderPurple,
  textDisabled: colors.emperorGrey,
  textHeading: colors.lavenderPurple300,
  textHighlight: colors.lavenderPurple500,

  /**
   * Background colors
   */
  backgroundPrimary: colors.professionalGrey,
  backgroundSecondary: colors.mineShaftGrey,
  backgroundTertiary: colors.poweringGreen,
  backgroundLightOnDark: colors.emeraldGreen,
  backgroundLightOnDarkGradient: colors.oceanEmerald,
  backgroundSuccess: colors.hyperGreen,
  backgroundInfo: colors.babyBlue,
  backgroundAlert: colors.blushRed,
  backgroundWarning: colors.miniYellow,
  backgroundPositive: colors.plumPurple,

  /**
   * Border colors
   */
  borderPrimary: colors.sonicGrey,
  borderSuccess: colors.emeraldGreen,
  borderInfo: colors.broadBlue,
  borderAlert: colors.rubyRed,
  borderWarning: colors.inkGrey,
  borderPositive: colors.paleLavenderPurple,

  /**
   * Chart colors
   */
  'chart1-1': colors.ivyGreen400,
  'chart1-2': colors.poweringGreen,
  'chart1-3': colors.ivyGreen200,
  'chart2-1': colors.happyPurple,
  'chart2-2': colors.orchidPurple,
  'chart2-3': colors.lavenderPurple300,
  'chart3-1': colors.smurfBlue,
  'chart3-2': colors.waterBlue,
  'chart3-3': colors.babyBlue,
  'chart4-1': colors.powerRed600,
  'chart4-2': colors.softRed,
  'chart4-3': colors.blushRed,
  'chart5-1': colors.fineOrange,
  'chart5-2': colors.energeticOrange,
  'chart5-3': colors.stoicalOrange,
  'chart6-1': colors.calmTurquoise,
  'chart6-2': colors.turquoiseGreen,
  'chart6-3': colors.hyperGreen,
  'chart7-1': colors.cloudGrey,
  'chart7-2': colors.silverGrey,
  'chart7-3': colors.sonicGrey,

  /** DEPRECATED */
  chart1: colors.poweringGreen,
  chart2: colors.lavenderPurple,
  chart3: colors.waterBlue,
  chart4: colors.energeticOrange,
  chart5: colors.softRed,
  chart6: colors.fallRedOrange,
  chart7: colors.electricGreen,
  chart8: colors.sunsetBeige,
  chart9: colors.fogGrey,
  chart10: colors.lightGrey,
}

/**
 * Modifies the color for the hover state.
 */
export const getHoverColor = (theme: Theme, color: string): string =>
  generateColorLuminances('hover', theme, { color }).bg

/**
 * Modifies the color for the click (active) state.
 */
export const getClickColor = (theme: Theme, color: string): string =>
  generateColorLuminances('click', theme, { color }).bg

/**
 * Modifies the color for the focus state.
 */
export const getFocusColor = (theme: Theme, color: string): string =>
  generateColorLuminances('focus', theme, { color }).bg
